import { Loader2, Pause, Play, Radio, Square } from 'lucide-react'
import type { Channel, StreamStatusResponse } from '../../../api'
import { IconButton } from '../../../components/ui'
import styles from './ChannelStreamInlineControls.module.css'
import streamIndicators from '../styles/streamIndicators.module.css'

type ChannelStreamInlineControlsProps = {
  channel: Channel
  loading: boolean
  playbackLoading: boolean
  playbackPlaying: boolean
  starting: boolean
  status: StreamStatusResponse | undefined
  stopping: boolean
  onStart: (channel: Channel) => Promise<void>
  onStop: (channel: Channel) => Promise<void>
  onTogglePlayback: (channel: Channel) => void
}

export const ChannelStreamInlineControls = ({
  channel,
  loading,
  playbackLoading,
  playbackPlaying,
  starting,
  status,
  stopping,
  onStart,
  onStop,
  onTogglePlayback
}: ChannelStreamInlineControlsProps) => {
  const live = status?.status === 'live'

  if (loading) {
    return (
      <div className={styles.root} aria-label={`Stream controls for ${channel.name}`}>
        <span className={styles.skeleton} aria-label={`Loading ${channel.name} stream status`}>
          <span />
          <span />
        </span>
      </div>
    )
  }

  if (live) {
    return (
      <div className={styles.root} aria-label={`Stream controls for ${channel.name}`}>
        <span
          className={[streamIndicators.statusDot, streamIndicators.live].join(' ')}
          aria-label="Stream live"
          title="Stream live"
        />
        <IconButton
          className={styles.listenButton}
          disabled={playbackLoading}
          icon={playbackLoading ? <span className="track-status-spinner" /> : playbackPlaying ? <Pause size={15} /> : <Play size={15} />}
          iconSize="play"
          label={playbackLoading
            ? `Loading ${channel.name} stream`
            : playbackPlaying
              ? `Pause ${channel.name} stream`
              : `Listen to ${channel.name} stream`}
          loading={playbackLoading}
          onClick={() => onTogglePlayback(channel)}
          title={playbackLoading ? 'Loading stream' : playbackPlaying ? 'Pause stream' : 'Listen to stream'}
        />
        <IconButton
          className={styles.stopButton}
          disabled={stopping}
          icon={stopping ? <Loader2 className="track-status-spinner" size={15} /> : <Square size={14} />}
          label={`Stop ${channel.name} stream`}
          onClick={() => void onStop(channel)}
          title="Stop stream"
        />
      </div>
    )
  }

  return (
    <div className={styles.root} aria-label={`Stream controls for ${channel.name}`}>
      <span
        className={[streamIndicators.statusDot, streamIndicators.stopped].join(' ')}
        aria-label="Stream stopped"
        title="Stream stopped"
      />
      <IconButton
        className={styles.startButton}
        disabled={starting}
        icon={starting ? <Loader2 className="track-status-spinner" size={15} /> : <Radio size={15} />}
        label={`Start ${channel.name} stream`}
        onClick={() => void onStart(channel)}
        title="Start stream"
      />
    </div>
  )
}
